import {type FC, useRef} from 'react';
import {CSSTransition} from 'react-transition-group';

import styled from 'styled-components';

import type {
  AlertButton,
  ButtonsContainerStyledProps,
  ViewProps,
} from './types';

import {View} from '../../../../uikit/View';
import {truncateStringByWord} from '../../../../utils/format';
import {useController} from './controller';
import {Icon} from './Icon';
import {
  Animation,
  AnimationContainer,
  Button,
  buttonsContainerWeb,
  ButtonV2,
  CloseButton,
  CloseIcon,
  containerWeb,
  Message,
  messageContainerWeb,
  Title,
  TitleContainer,
} from './styles';

const Container = styled(View)<{
  $animationDuration: number;
  $variant?: 'notification' | 'promo';
}>`
  ${containerWeb};
`;

const MessageContainer = styled(View)<{$variant?: 'notification' | 'promo'}>`
  ${messageContainerWeb}
`;

const ButtonsContainer = styled(View)<ButtonsContainerStyledProps>`
  ${buttonsContainerWeb};
`;

export const Alert: FC<ViewProps> = props => {
  const {
    afterButtonsSlot,
    animation,
    animationDuration,
    animationSize = 160,
    beforeButtonsSlot,
    beforeMessageSlot,
    beforeTitleSlot,
    buttons,
    buttonsV2,
    icon,
    iconColor = 'icon3',
    iconSize,
    isDismissible,
    isHiding,
    testID,
    titleAlign = 'center',
    variant,
  } = props;

  const nodeRef = useRef<HTMLDivElement>(null);

  const {onButtonClick, onCloseButtonClick} = useController(props);

  const title = props.title ? (
    <Title align={titleAlign}>{truncateStringByWord(props.title, 250)}</Title>
  ) : (
    props.renderTitle?.() || null
  );

  const message = props.message ? (
    <Message>{props.message}</Message>
  ) : (
    props.renderMessage?.() || null
  );

  const buttonsLen = buttons?.length || buttonsV2?.length || 0;

  const beforeTitleContent = beforeTitleSlot?.() || null;

  return (
    <CSSTransition
      appear
      classNames="alert"
      in={!isHiding}
      nodeRef={nodeRef}
      timeout={animationDuration}
    >
      <Container
        $animationDuration={animationDuration}
        $variant={variant}
        ref={nodeRef}
        testID={testID || 'Alert'}
      >
        {icon ? <Icon color={iconColor} size={iconSize} svg={icon} /> : null}
        {animation ? (
          <AnimationContainer>
            <Animation size={animationSize} {...animation} />
          </AnimationContainer>
        ) : null}
        {beforeTitleContent}
        {title ? (
          <TitleContainer
            withPaddingForCloseButton={
              isDismissible && !icon && !beforeTitleContent
            }
          >
            {title}
          </TitleContainer>
        ) : null}
        {beforeMessageSlot?.() || null}
        {message ? (
          <MessageContainer $variant={variant}>{message}</MessageContainer>
        ) : null}
        {beforeButtonsSlot?.() || null}
        {isDismissible ? (
          <CloseButton onClick={onCloseButtonClick}>
            <CloseIcon />
          </CloseButton>
        ) : null}
        {variant === 'notification' ? null : (
          <ButtonsContainer $buttonsCount={buttonsLen} $variant={variant}>
            {buttons?.map((button, i, itself) => (
              <Button
                $buttonsCount={buttonsLen}
                $isFirst={i === 0}
                $isLast={i === itself.length - 1}
                $variant={variant}
                disabled={button.isDisabled}
                icon={button.icon}
                iconColor={button.iconColor}
                key={button.label}
                onClick={() => onButtonClick(button)}
                scheme={button.scheme || 'gray'}
                testID={button.testID}
              >
                {button.label}
              </Button>
            ))}

            {buttonsV2?.map(
              (
                {hideAlertOnClick, label, onAwaitableClick, onClick, ...rest},
                i,
                itself,
              ) => (
                <ButtonV2
                  $buttonsCount={buttonsLen}
                  $isFirst={i === 0}
                  $isLast={i === itself.length - 1}
                  $variant={variant}
                  key={label}
                  onClick={() =>
                    onButtonClick({
                      hideAlertOnClick,
                      onAwaitableClick,
                      onClick,
                    } as AlertButton)
                  }
                  {...rest}
                >
                  {label}
                </ButtonV2>
              ),
            )}
          </ButtonsContainer>
        )}
        {afterButtonsSlot?.() || null}
      </Container>
    </CSSTransition>
  );
};
